/**
 * Column Configuration Utility
 * Manages column visibility, order, and presets for Live View, History Aircraft, and History Tracks tables.
 */

const STORAGE_KEY = 'columnConfig';
const CONFIG_VERSION = 4;

// ===== COLUMN DEFINITIONS =====

// Live View columns (DataTable.js)
export const LIVE_VIEW_COLUMNS = [
  { key: 'flag', label: 'Flag', canHide: false },
  { key: 'hex', label: 'Hex ID', canHide: true },
  { key: 'r', label: 'Reg', canHide: true },
  { key: 'airline_logo', label: 'Operator', canHide: true },
  { key: 'airline_icao', label: 'Airline ICAO', canHide: true },
  { key: 'airline_name', label: 'Airline Name', canHide: true },
  { key: 'operator', label: 'Operator Name', canHide: true },
  { key: 'flight', label: 'Callsign', canHide: true },
  { key: 'flight_iata', label: 'Flight', canHide: true },
  { key: 'dest_airport', label: 'Dest', canHide: true },
  { key: 'eta_minutes', label: 'ETA', canHide: true },
  { key: 'aircraft_type', label: 'Type', canHide: true },
  { key: 'desc', label: 'Description', canHide: true },
  { key: 'category', label: 'Category', canHide: true },
  { key: 'squawk', label: 'Squawk', canHide: true },
  { key: 'alt_baro', label: 'Altitude', canHide: true },
  { key: 'baro_rate', label: 'V/S', canHide: true },
  { key: 'gs', label: 'Speed', canHide: true },
  { key: 'true_heading', label: 'Heading', canHide: true },
  { key: 'track', label: 'Track', canHide: true },
  { key: 'r_dst', label: 'Distance', canHide: true },
  { key: 'lon', label: 'Longitude', canHide: true },
  { key: 'lat', label: 'Latitude', canHide: true },
  { key: 'country', label: 'Country', canHide: true },
  { key: 'messages', label: 'Messages', canHide: true },
  { key: 'rssi', label: 'RSSI', canHide: true },
  { key: 'last_seen_at', label: 'Last Seen', canHide: true },
  { key: 'military', label: 'Military', canHide: true },
  { key: 'inactivityTime', label: 'Inactive', canHide: true },
  { key: 'age', label: 'Age', canHide: true }
];

// History Aircraft columns (History.js)
export const HISTORY_AIRCRAFT_COLUMNS = [
  { key: 'flag', label: 'Flag', canHide: false },
  { key: 'icao24', label: 'Hex ID', canHide: true },
  { key: 'registration', label: 'Reg', canHide: true },
  { key: 'callsign', label: 'Callsign', canHide: true },
  { key: 'airline', label: 'Operator', canHide: true },
  { key: 'airlineCode', label: 'Airline ICAO', canHide: true },
  { key: 'airlineName', label: 'Operator Name', canHide: true },
  { key: 'aircraftType', label: 'Type', canHide: true },
  { key: 'aircraftDescription', label: 'Description', canHide: true },
  { key: 'countryCode', label: 'Country Code', canHide: true },
  { key: 'country', label: 'Country', canHide: true },
  { key: 'trackCount', label: 'Tracks', canHide: true },
  { key: 'firstSeen', label: 'First Seen', canHide: true },
  { key: 'lastSeen', label: 'Last Seen', canHide: true },
  { key: 'totalTimeTrackedSeconds', label: 'Duration', canHide: true },
  { key: 'furthestRangeKm', label: 'Max Distance', canHide: true },
  { key: 'totalMessages', label: 'Messages', canHide: true },
  { key: 'military', label: 'Military', canHide: true },
  { key: 'status', label: 'Status', canHide: true }
];

// History Tracks columns (History.js)
export const HISTORY_TRACKS_COLUMNS = [
  { key: 'callsign', label: 'Callsign', canHide: false },
  { key: 'startTime', label: 'Start', canHide: true },
  { key: 'endTime', label: 'End', canHide: true },
  { key: 'durationSeconds', label: 'Duration', canHide: true },
  { key: 'maxAltitudeFeet', label: 'Max Altitude', canHide: true },
  { key: 'maxDistanceKm', label: 'Max Distance', canHide: true },
  { key: 'minDistanceKm', label: 'Min Distance', canHide: true },
  { key: 'totalPositions', label: 'Positions', canHide: true },
  { key: 'totalMessages', label: 'Messages', canHide: true }
];

// ===== PRESETS =====

const PRESETS = {
  minimal: {
    liveView: {
      visibleColumns: ['flag', 'flight', 'aircraft_type', 'alt_baro', 'r_dst'],
      columnOrder: ['flag', 'flight', 'aircraft_type', 'alt_baro', 'r_dst']
    },
    historyAircraft: {
      visibleColumns: ['flag', 'registration', 'airline', 'aircraftType', 'trackCount', 'status'],
      columnOrder: ['flag', 'registration', 'airline', 'aircraftType', 'trackCount', 'status']
    },
    historyTracks: {
      visibleColumns: ['callsign', 'startTime', 'durationSeconds', 'maxAltitudeFeet'],
      columnOrder: ['callsign', 'startTime', 'durationSeconds', 'maxAltitudeFeet']
    }
  },
  standard: {
    liveView: {
      visibleColumns: ['flag', 'hex', 'r', 'airline_logo', 'flight', 'flight_iata', 'dest_airport', 'eta_minutes', 'aircraft_type', 'squawk', 'alt_baro', 'baro_rate', 'gs', 'r_dst', 'lon', 'lat', 'messages', 'rssi', 'age'],
      columnOrder: ['flag', 'hex', 'r', 'airline_logo', 'flight', 'flight_iata', 'dest_airport', 'eta_minutes', 'aircraft_type', 'squawk', 'alt_baro', 'baro_rate', 'gs', 'r_dst', 'lon', 'lat', 'messages', 'rssi', 'age']
    },
    historyAircraft: {
      visibleColumns: ['flag', 'icao24', 'registration', 'callsign', 'airline', 'aircraftType', 'trackCount', 'firstSeen', 'lastSeen', 'totalTimeTrackedSeconds', 'furthestRangeKm', 'totalMessages', 'status'],
      columnOrder: ['flag', 'icao24', 'registration', 'callsign', 'airline', 'aircraftType', 'trackCount', 'firstSeen', 'lastSeen', 'totalTimeTrackedSeconds', 'furthestRangeKm', 'totalMessages', 'status']
    },
    historyTracks: {
      visibleColumns: ['callsign', 'startTime', 'endTime', 'durationSeconds', 'maxAltitudeFeet', 'maxDistanceKm', 'minDistanceKm', 'totalPositions', 'totalMessages'],
      columnOrder: ['callsign', 'startTime', 'endTime', 'durationSeconds', 'maxAltitudeFeet', 'maxDistanceKm', 'minDistanceKm', 'totalPositions', 'totalMessages']
    }
  },
  full: {
    liveView: {
      visibleColumns: LIVE_VIEW_COLUMNS.map(c => c.key),
      columnOrder: LIVE_VIEW_COLUMNS.map(c => c.key)
    },
    historyAircraft: {
      visibleColumns: HISTORY_AIRCRAFT_COLUMNS.map(c => c.key),
      columnOrder: HISTORY_AIRCRAFT_COLUMNS.map(c => c.key)
    },
    historyTracks: {
      visibleColumns: HISTORY_TRACKS_COLUMNS.map(c => c.key),
      columnOrder: HISTORY_TRACKS_COLUMNS.map(c => c.key)
    }
  }
};

// ===== DEFAULT CONFIG =====

const getDefaultConfig = () => ({
  version: CONFIG_VERSION,
  activePreset: 'standard',
  tables: { ...PRESETS.standard }
});

// ===== STORAGE FUNCTIONS =====

/**
 * Load column configuration from localStorage
 */
export function getColumnConfig() {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (!stored) {
      return getDefaultConfig();
    }

    const config = JSON.parse(stored);

    // Version check - reset if outdated
    if (config.version !== CONFIG_VERSION) {
      return getDefaultConfig();
    }

    return config;
  } catch (e) {
    console.error('Error loading column config:', e);
    return getDefaultConfig();
  }
}

/**
 * Save column configuration to localStorage
 */
export function saveColumnConfig(config) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify({
      ...config,
      version: CONFIG_VERSION
    }));
    return true;
  } catch (e) {
    console.error('Error saving column config:', e);
    return false;
  }
}

/**
 * Apply a preset to the configuration
 */
export function applyPreset(presetName) {
  const preset = PRESETS[presetName];
  if (!preset) {
    console.warn(`Unknown preset: ${presetName}`);
    return null;
  }

  const config = {
    version: CONFIG_VERSION,
    activePreset: presetName,
    tables: { ...preset }
  };

  saveColumnConfig(config);
  return config;
}

/**
 * Update a single table's configuration
 */
export function updateTableConfig(tableName, visibleColumns, columnOrder) {
  const config = getColumnConfig();

  config.tables[tableName] = {
    visibleColumns,
    columnOrder
  };

  // Mark as custom since user made manual changes
  config.activePreset = 'custom';

  saveColumnConfig(config);
  return config;
}

/**
 * Get configuration for a specific table
 */
export function getTableConfig(tableName) {
  const config = getColumnConfig();
  return config.tables[tableName] || PRESETS.standard[tableName];
}

/**
 * Get all available presets
 */
export function getPresetNames() {
  return ['minimal', 'standard', 'full', 'custom'];
}

/**
 * Get preset display labels
 */
export function getPresetLabels() {
  return {
    minimal: 'Minimal',
    standard: 'Standard',
    full: 'Full',
    custom: 'Custom'
  };
}

/**
 * Get column definitions for a specific table
 */
export function getColumnDefinitions(tableName) {
  switch (tableName) {
    case 'liveView':
      return LIVE_VIEW_COLUMNS;
    case 'historyAircraft':
      return HISTORY_AIRCRAFT_COLUMNS;
    case 'historyTracks':
      return HISTORY_TRACKS_COLUMNS;
    default:
      return [];
  }
}

/**
 * Utility: Reorder array by moving an item from one index to another
 */
export function reorderArray(array, fromIndex, toIndex) {
  const result = [...array];
  const [removed] = result.splice(fromIndex, 1);
  result.splice(toIndex, 0, removed);
  return result;
}

/**
 * Filter and order columns based on configuration
 * @param {Array} allColumns - All column definitions from the table component
 * @param {Object} tableConfig - Configuration for the table { visibleColumns, columnOrder }
 * @returns {Array} Filtered and ordered columns
 */
export function filterAndOrderColumns(allColumns, tableConfig) {
  if (!tableConfig) return allColumns;

  const { visibleColumns, columnOrder } = tableConfig;

  // Create order map for sorting
  const orderMap = new Map(columnOrder.map((key, idx) => [key, idx]));

  // Filter by visibility and sort by order
  return allColumns
    .filter(col => visibleColumns.includes(col.key))
    .sort((a, b) => {
      const aOrder = orderMap.get(a.key) ?? 999;
      const bOrder = orderMap.get(b.key) ?? 999;
      return aOrder - bOrder;
    });
}
